import React, { useContext, useEffect } from "react";
import { Box, ButtonBase, Paper, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import ListAltIcon from "@mui/icons-material/ListAlt";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import SchoolIcon from "@mui/icons-material/School";

import Application from "../../components/application";
import MyLoading from "../../components/myLoading";
import { AuthContext } from "../../contexts/auth";
import TELAS from "../../constants/telas";
import TELA_ACOES from "../../constants/telaAcoes";
import useDashboardState from "../../hooks/useDashboard";

const MODULOS = [
  { titulo: "Cursos", descricao: "Treinamentos e desenvolvimento da equipe", cor: "#2f6fa5", itens: [
    { chave: "Cursos", texto: "Cursos", descricao: "Gerencie cursos e conteúdos", icone: SchoolIcon, rota: "/gestor/cursos", tela: TELAS.CURSOS, app: "SisPalo - Colaborador" },
    { chave: "Cursos", texto: "Colaboradores", descricao: "Acompanhe os colaboradores", icone: PersonIcon, rota: "/gestor/colaboradores", tela: TELAS.COLABORADORES, app: "SisPalo - Colaborador" },
  ] },
  { titulo: "Tarefas", descricao: "Operação, acompanhamento e controle", cor: "#32477d", itens: [
    { chave: "Tarefas", texto: "Tarefas", descricao: "Acompanhe tarefas em execução", icone: AssignmentTurnedInIcon, rota: "/gestor/tarefas", tela: TELAS.TAREFAS, app: "SisPalo - Tarefas" },
    { chave: "Tarefas", texto: "Checklist de veículos", descricao: "Consulte inspeções de veículos", icone: DirectionsCarIcon, rota: "/gestor/checklist-veiculos", tela: TELAS.TAREFAS, app: "SisPalo - Tarefas" },
    { chave: "Tarefas", texto: "Templates de tarefas", descricao: "Configure modelos e formulários", icone: ContentCopyIcon, rota: "/gestor/template", tela: TELAS.TEMPLATE_TAREFAS, app: "SisPalo - Tarefas" },
    { chave: "Tarefas", texto: "Locais de tarefa", descricao: "Cadastre postos e locais", icone: AccountBalanceIcon, rota: "/gestor/localtarefa", tela: TELAS.LOCAIS_DE_TAREFAS, app: "SisPalo - Tarefas" },
  ] },
  // Indicadores ficam separados das operações para manter o menu organizado.
  { titulo: "Indicadores", descricao: "Resumos e estatísticas operacionais", cor: "#546b87", itens: [
    { chave: "Tarefas", texto: "Dashboard de tarefas", descricao: "Veja locais visitados e pendentes", icone: AssessmentIcon, rota: "/gestor/dashboard-tarefas", tela: TELAS.TAREFAS, app: "SisPalo - Tarefas" },
  ] },
  { titulo: "Gestão", descricao: "Relatórios e recursos administrativos", cor: "#53735d", itens: [
    // Permanece no grupo Gestão, mas segue a permissão de consulta de Tarefas.
    { chave: "Tarefas", texto: "Acompanhamento de usuários", descricao: "Veja onde os usuários estão", icone: LocationOnIcon, rota: "/gestor/acompanhamento", tela: TELAS.TAREFAS, app: "SisPalo - Tarefas" },
    { chave: "Gestor", texto: "Agendar relatórios", descricao: "Programe o envio de relatórios", icone: ListAltIcon, rota: "/gestor/relatorios", tela: TELAS.AGENDA_RELATORIOS, app: "SisPalo - Gestor" },
  ] },
];

const DashboardCard = ({ item, cor, onClick }) => {
  const Icone = item.icone;
  return (
    <ButtonBase onClick={onClick} sx={{ borderRadius: 2, textAlign: "left", width: "100%" }}>
      <Paper variant="outlined" sx={{
        width: "100%", minHeight: 86, p: 1.25, borderRadius: 2, display: "flex",
        gap: 1, alignItems: "center",
        transition: "transform .2s ease, box-shadow .2s ease, border-color .2s ease",
        "&:hover": { transform: "translateY(-3px)", boxShadow: 4, borderColor: cor },
      }}>
        <Box sx={{ bgcolor: cor, color: "white", borderRadius: 1.5, p: .8, display: "flex", flexShrink: 0 }}>
          <Icone fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body1" fontWeight={700} lineHeight={1.2}>{item.texto}</Typography>
          <Typography variant="caption" color="text.secondary" lineHeight={1.25}>{item.descricao}</Typography>
        </Box>
      </Paper>
    </ButtonBase>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const states = useDashboardState();
  const { user, permissaoListaFuncionalidade, permissaoUsuario } = useContext(AuthContext);

  useEffect(() => { states.submit.set(); }, []);

  const permitido = (item) => permissaoListaFuncionalidade.value.includes(item.chave)
    && permissaoUsuario.set(item.app, item.chave, item.tela, TELA_ACOES.Consultar);
  const primeiroNome = user.value?.name?.trim().split(/\s+/)[0];

  return (
    <Application>
      <Box sx={{ maxWidth: 1280, mx: "auto", p: { xs: .5, sm: 1.5 } }}>
        <MyLoading render={states.submit.value} />
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, boxShadow: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1}>
            <Box>
              <Typography variant="h4" fontWeight={800}>Dashboard</Typography>
              <Typography color="text.secondary" sx={{ mt: .5 }}>
                {primeiroNome ? `Olá, ${primeiroNome}. ` : ""}Selecione uma área para continuar.
              </Typography>
            </Box>
          </Stack>

          <Stack spacing={2.5} sx={{ mt: 2.5 }}>
            {MODULOS.map((modulo) => {
              const itens = modulo.itens.filter(permitido);
              if (!itens.length) return null;
              return (
                <Box key={modulo.titulo}>
                  <Typography variant="h6" fontWeight={700}>{modulo.titulo}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{modulo.descricao}</Typography>
                  <Box sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                      md: "repeat(3, minmax(0, 1fr))",
                      lg: "repeat(4, minmax(0, 1fr))",
                    },
                    gap: 1.25,
                  }}>
                    {itens.map((item) => <DashboardCard key={item.rota} item={item} cor={modulo.cor} onClick={() => navigate(item.rota)} />)}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Paper>
      </Box>
    </Application>
  );
};

export default Dashboard;
